import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Clock, Euro, Zap, ArrowLeft, Send } from "lucide-react";

export default function JobDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);

    useEffect(() => {
        api.get(`/jobs/${id}`).then(({ data }) => setJob(data.data)).catch(() => nav(-1));
    }, [id]);

    if (!job) return <div>Caricamento…</div>;

    const apply = async () => {
        try {
            await api.post("/applications", { job_id: job.id });
            toast.success("Candidatura inviata");
            nav("/app/candidature");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="max-w-3xl mx-auto">
            <Button variant="ghost" onClick={() => nav(-1)} className="mb-4"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</Button>
            <div className="hl-card p-8">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="hl-pill">{job.profession}</span>
                            {job.urgent && <span className="hl-pill bg-[#FDF4F2] text-[#D96C5B] border-[#D96C5B]/30"><Zap className="w-3 h-3" />Urgente</span>}
                            <Badge variant="outline">{job.status}</Badge>
                        </div>
                        <h1 className="mt-4 font-display text-3xl font-bold tracking-tight">{job.title}</h1>
                        <div className="text-[#5C5C5C] mt-1">{job.facility_name}</div>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-8">
                    <div className="p-4 bg-[#F9F9F7] rounded-md">
                        <div className="flex items-center gap-2 text-xs text-[#5C5C5C]"><MapPin className="w-3 h-3" />LUOGO</div>
                        <div className="mt-2 font-semibold">{job.city || "—"}</div>
                    </div>
                    <div className="p-4 bg-[#F9F9F7] rounded-md">
                        <div className="flex items-center gap-2 text-xs text-[#5C5C5C]"><Clock className="w-3 h-3" />QUANDO</div>
                        <div className="mt-2 font-semibold">{job.date}</div>
                        <div className="text-sm text-[#5C5C5C]">{job.start_time}–{job.end_time}</div>
                    </div>
                    <div className="p-4 bg-[#F9F9F7] rounded-md">
                        <div className="flex items-center gap-2 text-xs text-[#5C5C5C]"><Euro className="w-3 h-3" />COMPENSO</div>
                        <div className="mt-2 font-semibold font-mono">{job.compensation_gross?.toFixed(2)}€ <span className="text-xs font-normal text-[#5C5C5C]">lordo</span></div>
                    </div>
                </div>
                <div className="mt-8">
                    <h2 className="font-display font-bold text-lg">Descrizione</h2>
                    <p className="mt-2 text-[#5C5C5C] whitespace-pre-line">{job.description}</p>
                </div>
                {job.requirements && (
                    <div className="mt-6">
                        <h2 className="font-display font-bold text-lg">Requisiti</h2>
                        <p className="mt-2 text-[#5C5C5C] whitespace-pre-line">{job.requirements}</p>
                    </div>
                )}
                {user?.role === "professional" && job.status === "PUBLISHED" && (
                    <div className="mt-8 flex justify-end">
                        <Button onClick={apply} size="lg" className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="apply-detail-btn">
                            <Send className="w-4 h-4 mr-2" />Candidati ora
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
